/**
 * Device Capability Detection Hook
 *
 * Detects device performance capabilities and connection quality
 * to optimize animations and user experience accordingly.
 *
 * @example
 * const { deviceTier, connectionQuality, isMobile, capabilities } = useDeviceCapabilities();
 */

import { useState, useEffect, useMemo } from 'react';

export interface DeviceCapabilities {
  // Hardware capabilities
  cpuCores: number;
  deviceMemory?: number;
  screenWidth: number;
  screenHeight: number;
  pixelRatio: number;
  touchSupport: boolean;

  // Performance indicators
  isLowEndDevice: boolean;
  isHighEndDevice: boolean;
  isMidRangeDevice: boolean;

  // Connection quality
  connectionSpeed?: 'slow' | 'fast' | 'unknown';
  effectiveType?: string;

  // Device type
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

export type DeviceTier = 'high-end' | 'mid-range' | 'low-end' | 'mobile';

export interface AnimationConfig {
  // Base durations
  duration: {
    fast: number;
    normal: number;
    slow: number;
  };

  // Stagger delays
  stagger: {
    fast: number;
    normal: number;
    slow: number;
  };

  // Easing functions (cubic-bezier tuples — motion v12 requires a 4-number tuple, not number[])
  easing: {
    smooth: [number, number, number, number];
    bounce: [number, number, number, number];
    sharp: [number, number, number, number];
  };

  // Reduced motion settings
  reducedMotion: boolean;

  // Hardware acceleration
  useHardwareAcceleration: boolean;

  // Lazy loading
  enableLazyAnimations: boolean;
}

const getDeviceTier = (capabilities: DeviceCapabilities): DeviceTier => {
  // Mobile devices get special treatment
  if (capabilities.isMobile) {
    return 'mobile';
  }

  // High-end devices (4+ CPU cores, high memory)
  if (capabilities.cpuCores >= 4 && capabilities.deviceMemory && capabilities.deviceMemory >= 4) {
    return 'high-end';
  }

  // Low-end devices (1-2 CPU cores, low memory)
  if (capabilities.cpuCores <= 2) {
    return 'low-end';
  }

  // Mid-range devices (3 CPU cores or moderate specs)
  return 'mid-range';
};

const getAnimationConfig = (tier: DeviceTier, reducedMotion: boolean): AnimationConfig => {
  const baseConfigs = {
    'high-end': {
      duration: { fast: 0.3, normal: 0.6, slow: 0.8 },
      stagger: { fast: 0.05, normal: 0.1, slow: 0.15 },
    },
    'mid-range': {
      duration: { fast: 0.25, normal: 0.45, slow: 0.6 },
      stagger: { fast: 0.08, normal: 0.12, slow: 0.18 },
    },
    'low-end': {
      duration: { fast: 0.15, normal: 0.3, slow: 0.4 },
      stagger: { fast: 0.1, normal: 0.15, slow: 0.2 },
    },
    'mobile': {
      duration: { fast: 0.2, normal: 0.35, slow: 0.5 },
      stagger: { fast: 0.08, normal: 0.12, slow: 0.16 },
    },
  };

  const config = baseConfigs[tier];

  return {
    ...config,
    easing: {
      smooth: [0.25, 0.46, 0.45, 0.94],
      bounce: [0.68, -0.55, 0.265, 1.55],
      sharp: [0.4, 0, 0.2, 1],
    },
    reducedMotion,
    useHardwareAcceleration: tier !== 'low-end',
    enableLazyAnimations: tier === 'low-end' || tier === 'mobile',
  };
};

export const useDeviceCapabilities = () => {
  const [capabilities, setCapabilities] = useState<DeviceCapabilities>({
    cpuCores: navigator.hardwareConcurrency || 2,
    deviceMemory: (navigator as any).deviceMemory,
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    pixelRatio: window.devicePixelRatio || 1,
    touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    isLowEndDevice: false,
    isHighEndDevice: false,
    isMidRangeDevice: false,
    isMobile: false,
    isTablet: false,
    isDesktop: true,
  });

  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    // Detect device type
    const isMobile = window.innerWidth <= 768;
    const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
    const isDesktop = window.innerWidth > 1024;

    // Detect CPU cores and memory
    const cpuCores = navigator.hardwareConcurrency || 2;
    const deviceMemory = (navigator as any).deviceMemory;

    // Detect performance tier
    const isLowEndDevice = cpuCores <= 2;
    const isHighEndDevice = cpuCores >= 4 && deviceMemory && deviceMemory >= 4;
    const isMidRangeDevice = !isLowEndDevice && !isHighEndDevice;

    // Detect connection quality
    let connectionSpeed: 'slow' | 'fast' | 'unknown' = 'unknown';
    let effectiveType: string | undefined;

    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      effectiveType = connection.effectiveType;
      connectionSpeed = connection.downlink < 1 ? 'slow' : 'fast';
    }

    // Detect reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    setCapabilities({
      cpuCores,
      deviceMemory,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      pixelRatio: window.devicePixelRatio || 1,
      touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      isLowEndDevice,
      isHighEndDevice,
      isMidRangeDevice,
      connectionSpeed,
      effectiveType,
      isMobile,
      isTablet,
      isDesktop,
    });

    setReducedMotion(prefersReducedMotion);

    // Apply device class to document
    const deviceTier = getDeviceTier({
      ...capabilities,
      isMobile,
      isTablet,
      isDesktop,
      isLowEndDevice,
      isHighEndDevice,
      isMidRangeDevice,
    });

    document.documentElement.classList.add(`device-${deviceTier}`);

    // Apply connection class
    if (connectionSpeed !== 'unknown') {
      document.documentElement.classList.add(`connection-${connectionSpeed}`);
    }

    // Handle window resize
    const handleResize = () => {
      setCapabilities(prev => ({
        ...prev,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        isMobile: window.innerWidth <= 768,
        isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
        isDesktop: window.innerWidth > 1024,
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const deviceTier = useMemo(() => getDeviceTier(capabilities), [capabilities]);
  const animationConfig = useMemo(() => getAnimationConfig(deviceTier, reducedMotion), [deviceTier, reducedMotion]);

  return {
    capabilities,
    deviceTier,
    animationConfig,
    reducedMotion,
    connectionQuality: capabilities.connectionSpeed || 'unknown',
    isMobile: capabilities.isMobile,
    isTablet: capabilities.isTablet,
    isDesktop: capabilities.isDesktop,
  };
};

export default useDeviceCapabilities;
